import {
  useMemo,
  useState,
  useRef,
  forwardRef,
  ReactNode,
  MouseEventHandler
} from "react";
import DataTable from "react-data-table-component";
import { useDispatch, useSelector } from "react-redux";

import {
  Button,
  OverlayTrigger,
  Spinner,
  Stack,
  Tooltip,
  Dropdown,
  Modal,
} from "react-bootstrap";
import { T, useTranslate } from "@tolgee/react";
import { Toaster, toast } from "react-hot-toast";
import Symbol from "../../utils/symbol";
import IsLoadingIndicator from "../../isLoadingIndicator";
import axiosClient from "../../../utils/axios";
import { fetchPosts, setPageNumber } from "../../../redux/slices/postIdeas";
import { useClickAway } from "react-use";
import { getDateTimeFormated } from "../../utils/dates/utils";

type PostIdeasListDataTableProps = {
  query: Record<string, string | undefined>;
};

export function PostIdeasListDataTable({
  query,
}: PostIdeasListDataTableProps) {
  const { posts, isLoading, page, count } = useSelector(
    (state: any) => state.postIdeas
  );
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const [navigatingUrl, setNavigatingUrl] = useState<string | null>(null);

  const navigateTo = (url?: string) => {
    if (!url || navigatingUrl) {
      return;
    }
    setNavigatingUrl(url);
    window.location.href = url;
  };
  type CustomToggleProps = {
    children: ReactNode;
    onClick: MouseEventHandler<HTMLButtonElement>;
  };
  const CustomToggle = forwardRef<HTMLButtonElement, CustomToggleProps>(
    ({ children, onClick }, ref) => (
      <Button
        ref={ref}
        variant="white"
        size="sm"
        onClick={(e) => {
          e.preventDefault();
          onClick(e);
        }}
      >
        {children}
      </Button>
    )
  );
  CustomToggle.displayName = "PostIdeasActionsToggle";

  const columns = [
    {
      name: t("lomavisCreator.postIdeas.title"),
      show: true,
      grow: 2,
      key: "list_title",
      cell: (row) => (
        <Stack direction={"horizontal"} gap={3} style={{ padding: 4 }}>
          <div
            className="cursor-pointer"
            onClick={() => {
              navigateTo(row.creator_url);
            }}
          >
            <div className="p-0 m-0">
              <Symbol initials={row?.list_title?.[0] || "N"} />
            </div>
          </div>

          <a
            className="d-flex justify-content-start flex-column fw-bold text-primary"
            href={row.creator_url}
            onClick={(e) => {
              e.preventDefault();
              navigateTo(row.creator_url);
            }}
          >
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <>
                    <p className="text-gray-800 text-hover-primary mb-1 fs-6 cursor-pointer">
                      {row.list_title || t("lomavisCreator.postIdeas.no_title")}
                    </p>
                  </>
                </Tooltip>
              }
            >
              <div>
                <p className="text-gray-800 text-hover-primary mb-1 fs-6 cursor-pointer">
                  {row.list_title || t("lomavisCreator.postIdeas.no_title")}
                </p>
              </div>
            </OverlayTrigger>
          </a>
        </Stack>
      ),
    },
    {
      name: t("lomavisCreator.postIdeas.author_name"),
      show: true,
      key: "author_name",
      cell: (row) => (
        <div className="text-gray-800 text-hover-primary mb-1 fs-6">
          {row.author_name}
        </div>
      ),
    },
    {
      name: t("lomavisCreator.postIdeas.created_at"),
      show: true,
      key: "created_at",
      cell: (row) => (
        <div className="text-gray-800 text-hover-primary mb-1 fs-6">
          {row?.created ? getDateTimeFormated(row?.created) : t("-")}
        </div>
      ),
    },
    {
      name: t("lomavisCreator.postIdeas.action"),
      show: true,
      center: true,
      cell: (row) => {
        const [show, setShow] = useState(false);
        const handleDelete = (row) => {
          setShow(false);
          axiosClient
            .delete(row.api_url)
            .then((res) => {
              if (res.status === 204) {
                toast.success(t("lomavisCreator.postIdeas.delete_success"), {
                  position: "top-right",
                });

                dispatch(fetchPosts(page, query) as any);
              } else {
                toast.error(t("lomavisCreator.postIdeas.delete_error"), {
                  position: "top-right",
                });
              }
            })
            .catch(() => {
              toast.error(t("lomavisCreator.postIdeas.delete_error"), {
                position: "top-right",
              });
            });
        };
        const [showDropdown, setShowDropdown] = useState(false);
        const dropdownRef = useRef(null);
        useClickAway(dropdownRef, () => {
          setShowDropdown(false);
        });

        return (
          <div
            className="d-flex"
            onClick={(e) => e.stopPropagation()}
          >
            <Modal show={show} onHide={() => {}} size="sm" centered>
              <Modal.Header className="border-0">
                <Modal.Title>
                  {t("lomavisCreator.postIdeas.confirm")}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="border-0">
                {t("lomavisCreator.postIdeas.delete_user_confirmation")}
              </Modal.Body>
              <Modal.Footer className="border-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShow(false)}
                >
                  {t("lomavisCreator.postIdeas.close")}
                </Button>

                <Button
                  variant="danger"
                  size="sm"
                  onClick={() => handleDelete(row)}
                >
                  {t("lomavisCreator.postIdeas.delete")}
                </Button>
              </Modal.Footer>
            </Modal>
            <div className="d-flex">
              <Dropdown
                show={showDropdown}
                ref={dropdownRef}
                onClick={() => setShowDropdown(!showDropdown)}
                drop="up" // Attempting to make it a dropup.
              >
                <Dropdown.Toggle variant="light" size="sm" as={CustomToggle}>
                  ...
                </Dropdown.Toggle>

                <Dropdown.Menu
                  style={{
                    zIndex: 9999,
                    bottom: "100%", // Force the dropdown menu above the toggle.
                    top: "auto", // Ensure the top property doesn't interfere.
                  }}
                >
                  <Dropdown.Item
                    className="d-flex align-items-center py-3"
                    onClick={() => {
                      setShowDropdown(false);
                      navigateTo(row.creator_url);
                    }}
                  >
                    <i className="fa-duotone fa-pencil fs-5 me-3"></i>
                    {t("lomavisCreator.postIdeas.edit")}
                  </Dropdown.Item>
                  <Dropdown.Item
                    className="d-flex align-items-center py-3"
                    onClick={() => {
                      setShow(true);
                      setShowDropdown(false);
                    }}
                  >
                    <i className="fa-duotone fa-trash fs-5 me-3"></i>
                    {t("lomavisCreator.postIdeas.delete")}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>

              <Button
                className="ms-5"
                size="sm"
                variant="white"
                onClick={() => {
                  navigateTo(row.creator_url);
                }}
                disabled={!!navigatingUrl}
              >
                {navigatingUrl === row.creator_url ? (
                  <Spinner
                    animation="border"
                    size="sm"
                    role="status"
                    className="text-dark"
                  />
                ) : (
                  <i className="fa-duotone fa-caret-right fs-5 text-dark"></i>
                )}
              </Button>
            </div>
          </div>
        );
      },
    },
  ];

  const customStyles = {
    headCells: {
      style: {
        paddingLeft: "8px", // override the cell padding for head cells
        paddingRight: "8px",
        color: "gray",
      },
    },
    table: {
      style: {
        color: "red",
        backgroundColor: "gray",
        boxShadow: "none",
      },
    },
    rows: {
      style: {
        paddingTop: 10,
        paddingBottom: 10,
        cursor: "pointer",
        transition: "background-color 0.15s ease-in-out",
        "&:not(:last-of-type)": {
          borderBottomStyle: "dashed",
        },
        "&:hover": {
          backgroundColor: "#f5f8fa",
        },
      },
    },
    headRow: {
      style: {
        paddingTop: 10,
        paddingBottom: 10,

        borderBottomStyle: "dashed",
      },
    },
    pagination: {
      style: {
        borderTopStyle: "dashed",
      },
    },
  };
  const subHeaderComponentMemo = useMemo(() => {
    return (
      <div className="d-flex justify-content-start w-100 py-5  align-items-center">
        <div>
          <h2 style={{ margin: 0, paddingLeft: 0 }}>
            {t("lomavisCreator.postIdeas.collectedContent")}
          </h2>
        </div>
      </div>
    );
  }, []);
  return (
    <>
      <Toaster />
      <DataTable
        // onSort={handleSort}
        sortServer
        columns={columns}
        data={posts}
        subHeader
        subHeaderComponent={subHeaderComponentMemo}
        highlightOnHover
        pointerOnHover
        onRowClicked={(row) => {
          navigateTo(row?.creator_url);
        }}
        pagination
        paginationServer
        paginationIconLastPage={false}
        paginationTotalRows={count}
        onChangePage={(page) => {
          dispatch(setPageNumber(page));
        }}
        paginationComponentOptions={{
          noRowsPerPage: true,
          rangeSeparatorText: t("lomavisCreator.postIdeas.of"),
          rowsPerPageText: t("lomavisCreator.postIdeas.members_per_page"),
        }}
        progressPending={isLoading}
        paginationPerPage={10}
        // onChangeRowsPerPage={handleRowsPerPageChange}
        progressComponent={<IsLoadingIndicator />}
        paginationRowsPerPageOptions={[10, 15, 20]}
        customStyles={customStyles}
        // noTableCard
        noDataComponent={
          <T keyName="lomavisCreator.postIdeas.noDataAvailable"></T>
        }
      />
    </>
  );
}
